import { useEffect, useId, useRef } from "react";
import { createPortal } from "react-dom";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import { usePrefersReducedMotion } from "../usePrefersReducedMotion";
import type { ActivityGalleryImage } from "./galleryData";

type ActivityLightboxProps = {
  images: readonly ActivityGalleryImage[];
  index: number;
  onClose: () => void;
  onNavigate: (nextIndex: number) => void;
  returnFocusRef: React.RefObject<HTMLElement | null>;
};

/** Accessible lightbox for the Activities gallery. */
export function ActivityLightbox({
  images,
  index,
  onClose,
  onNavigate,
  returnFocusRef,
}: ActivityLightboxProps) {
  const reduced = usePrefersReducedMotion();
  const dialogRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const touchStartX = useRef<number | null>(null);
  const titleId = useId();
  const image = images[index];
  const total = images.length;

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    const returnFocus = returnFocusRef.current;
    document.body.style.overflow = "hidden";
    closeRef.current?.focus();

    return () => {
      document.body.style.overflow = previousOverflow;
      returnFocus?.focus();
    };
  }, [returnFocusRef]);

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        event.preventDefault();
        onClose();
      } else if (event.key === "ArrowLeft") {
        event.preventDefault();
        onNavigate((index - 1 + total) % total);
      } else if (event.key === "ArrowRight") {
        event.preventDefault();
        onNavigate((index + 1) % total);
      } else if (event.key === "Tab" && dialogRef.current) {
        const focusable = [
          ...dialogRef.current.querySelectorAll<HTMLElement>(
            'button:not([disabled]), [href], [tabindex]:not([tabindex="-1"])',
          ),
        ];
        if (focusable.length === 0) return;
        const first = focusable[0]!;
        const last = focusable[focusable.length - 1]!;
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }
    }

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [index, onClose, onNavigate, total]);

  if (!image || typeof document === "undefined") return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[70] flex items-end justify-center bg-forest-deep/92 p-3 pb-[max(5.5rem,env(safe-area-inset-bottom))] sm:items-center sm:p-6 sm:pb-6"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="relative flex w-full max-w-5xl flex-col overflow-hidden rounded-sm border border-cream/15 bg-forest-deep shadow-[var(--shadow-lift)]"
        onTouchStart={(event) => {
          touchStartX.current = event.changedTouches[0]?.clientX ?? null;
        }}
        onTouchEnd={(event) => {
          const start = touchStartX.current;
          const end = event.changedTouches[0]?.clientX;
          if (start == null || end == null) return;
          const delta = end - start;
          if (Math.abs(delta) < 48) return;
          if (delta > 0) onNavigate((index - 1 + total) % total);
          else onNavigate((index + 1) % total);
          touchStartX.current = null;
        }}
      >
        <div className="flex items-center justify-between gap-3 border-b border-cream/15 px-4 py-3 sm:px-5">
          <p id={titleId} className="text-sm font-semibold text-cream">
            {index + 1} of {total}
          </p>
          <button
            ref={closeRef}
            type="button"
            onClick={onClose}
            className="inline-flex size-11 items-center justify-center rounded-sm border border-cream/25 text-cream transition-colors hover:bg-cream/10"
            aria-label="Close image viewer"
          >
            <X className="size-5" aria-hidden="true" />
          </button>
        </div>

        <div className="relative flex min-h-[40vh] items-center justify-center bg-black/30 px-2 py-4 sm:px-12 sm:py-6">
          <button
            type="button"
            onClick={() => onNavigate((index - 1 + total) % total)}
            className="absolute left-2 top-1/2 z-10 inline-flex size-11 -translate-y-1/2 items-center justify-center rounded-sm border border-cream/25 bg-forest-deep/70 text-cream backdrop-blur-sm transition-colors hover:bg-cream/15 sm:left-3"
            aria-label="Previous image"
          >
            <ChevronLeft className="size-5" aria-hidden="true" />
          </button>

          <img
            key={image.id}
            src={image.fullSrc}
            alt={image.alt}
            width={image.width}
            height={image.height}
            decoding="async"
            className={
              reduced
                ? "max-h-[60vh] w-auto max-w-full object-contain sm:max-h-[68vh]"
                : "max-h-[60vh] w-auto max-w-full object-contain transition-opacity duration-300 sm:max-h-[68vh]"
            }
          />

          <button
            type="button"
            onClick={() => onNavigate((index + 1) % total)}
            className="absolute right-2 top-1/2 z-10 inline-flex size-11 -translate-y-1/2 items-center justify-center rounded-sm border border-cream/25 bg-forest-deep/70 text-cream backdrop-blur-sm transition-colors hover:bg-cream/15 sm:right-3"
            aria-label="Next image"
          >
            <ChevronRight className="size-5" aria-hidden="true" />
          </button>
        </div>

        <p className="border-t border-cream/15 px-4 py-4 text-sm leading-relaxed text-cream/85 sm:px-5">
          {image.caption}
        </p>
      </div>
    </div>,
    document.body,
  );
}
